import React, { useCallback, useState, useEffect } from 'react';
import { Form, message, Checkbox, Select, Empty, Spin, Upload, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FolderOutlined,
  FileTextOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { ACCEPT_FILE_EXTENSIONS } from './constants';
import { listMediaLibraryFiles } from '@refly-packages/ai-workspace-common/utils/media-library';

interface Folder {
  folderId: string;
  name: string;
  parentId?: string;
}

export interface MediaFile {
  fileId: string;
  name: string;
  type: string;
  size: number;
  storageKey?: string;
  folderId?: string;
  url?: string;
  source?: string;
  createdAt?: string;
  categoryName?: string;
  /** 素材库条目 ID；存在时变量值以 assetId 引用素材库，而不是 DriveFile。 */
  assetId?: string;
}

interface ResourceTypeFormProps {
  fileList: UploadFile[];
  uploading: boolean;
  onFileUpload: (file: File) => Promise<boolean>;
  onFileRemove: (file: UploadFile) => void;
  onRefreshFile?: () => void;
  onFilesSelect?: (files: MediaFile[]) => void;
  initialFiles?: MediaFile[];
  autoLoadLibrary?: boolean;
  form?: any;
  showError?: boolean;
  isRequired?: boolean;
}

export const ResourceTypeForm: React.FC<ResourceTypeFormProps> = React.memo(
  ({
    fileList,
    uploading,
    onFileUpload,
    onFileRemove,
    onFilesSelect,
    initialFiles = [],
    autoLoadLibrary = false,
    showError,
    isRequired = true,
  }) => {
    const { t } = useTranslation();

    const [folders, setFolders] = useState<Folder[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
    const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [loadingFolders, setLoadingFolders] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);

    // 素材来自统一素材库（MediaLibrary）；分类即条目所在文件夹的完整路径。
    const [libraryFiles, setLibraryFiles] = useState<MediaFile[]>([]);

    const fetchFolders = useCallback(async () => {
      setLoadingFolders(true);
      try {
        const entries = await listMediaLibraryFiles({ mediaKinds: ['image', 'video', 'audio'] });
        const files = entries.map((entry) => ({
          fileId: entry.entryId,
          assetId: entry.entryId,
          name: entry.name,
          type: entry.mimeType || '',
          size: Number(entry.size || 0),
          folderId: entry.path || '',
          source: 'media_library',
          createdAt: entry.createdAt,
        }));
        setLibraryFiles(files);
        const paths = [...new Set(files.map((file) => file.folderId).filter(Boolean))].sort();
        setFolders(paths.map((path) => ({ folderId: path, name: path })));
      } catch (error) {
        console.error('获取素材库失败:', error);
        message.error((error as Error)?.message || '获取素材库失败');
      } finally {
        setLoadingFolders(false);
      }
    }, []);

    const fetchFiles = useCallback(
      (folderId: string | null) => {
        setLoadingFiles(true);
        setMediaFiles(
          folderId ? libraryFiles.filter((file) => file.folderId === folderId) : libraryFiles,
        );
        setLoadingFiles(false);
      },
      [libraryFiles],
    );

    useEffect(() => {
      fetchFolders();
    }, [fetchFolders]);

    useEffect(() => {
      setSelectedFileIds(initialFiles.map((file) => file.fileId));
      if (autoLoadLibrary && selectedFolderId === null) {
        setSelectedFolderId('');
      }
    }, [initialFiles, autoLoadLibrary, selectedFolderId]);

    useEffect(() => {
      if (selectedFolderId !== null || autoLoadLibrary) {
        fetchFiles(selectedFolderId);
      }
    }, [selectedFolderId, fetchFiles, autoLoadLibrary]);

    const handleFolderChange = useCallback((folderId: string | null) => {
      setSelectedFolderId(folderId);
    }, []);

    // 分类名就是素材库文件夹路径；根目录文件归入「未分类」。
    const resolveCategoryName = useCallback(
      (file: MediaFile): string =>
        file.folderId || t('canvas.workflow.variables.uncategorized') || '未分类',
      [t],
    );

    const withCategoryName = useCallback(
      (files: MediaFile[]): MediaFile[] =>
        files.map((file) => ({ ...file, categoryName: resolveCategoryName(file) })),
      [resolveCategoryName],
    );

    const handleFileSelect = useCallback(
      (fileId: string, checked: boolean) => {
        setSelectedFileIds((prev) => {
          let newSelected: string[];
          if (checked) {
            newSelected = [...prev, fileId];
          } else {
            newSelected = prev.filter((id) => id !== fileId);
          }

          const selectedFiles = libraryFiles.filter((f) => newSelected.includes(f.fileId));
          if (onFilesSelect) {
            onFilesSelect(withCategoryName(selectedFiles));
          }

          return newSelected;
        });
      },
      [libraryFiles, onFilesSelect, withCategoryName],
    );

    const getFileIcon = (type: string) => {
      if (type.startsWith('image/')) return <PictureOutlined />;
      if (type.startsWith('video/')) return <VideoCameraOutlined />;
      return <FileTextOutlined />;
    };

    const getFileSize = (bytes: number) => {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const isFolderSelected = selectedFolderId !== null;

    const selectedInFolder = mediaFiles.filter((f) => selectedFileIds.includes(f.fileId)).length;
    const isAllSelected = mediaFiles.length > 0 && selectedInFolder === mediaFiles.length;
    const isPartiallySelected = selectedInFolder > 0 && selectedInFolder < mediaFiles.length;

    // 全选只作用于当前分类，其它分类里已勾选的素材保持不变。
    const handleSelectAll = useCallback(
      (checked: boolean) => {
        const folderFileIds = new Set(mediaFiles.map((f) => f.fileId));
        const newSelected = checked
          ? [...new Set([...selectedFileIds, ...folderFileIds])]
          : selectedFileIds.filter((id) => !folderFileIds.has(id));
        setSelectedFileIds(newSelected);
        if (onFilesSelect) {
          onFilesSelect(
            withCategoryName(libraryFiles.filter((f) => newSelected.includes(f.fileId))),
          );
        }
      },
      [libraryFiles, mediaFiles, onFilesSelect, selectedFileIds, withCategoryName],
    );

    const acceptAttr = ACCEPT_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');

    // Empty state considers both library selection and newly uploaded files
    const hasNoSelection = selectedFileIds.length === 0 && fileList.length === 0;

    return (
      <>
        <Form.Item
          required={isRequired}
          label={t('canvas.workflow.variables.value') || '变量值'}
          name="value"
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <div className="text-sm text-refly-text-0 font-medium">
                {t('canvas.workflow.variables.selectCategory') || '选择分类'}
              </div>
              <Select
                placeholder={
                  t('canvas.workflow.variables.selectCategoryPlaceholder') || '请选择分类'
                }
                value={selectedFolderId ?? undefined}
                onChange={(value) => handleFolderChange(value ?? null)}
                loading={loadingFolders}
                style={{ width: '100%' }}
                allowClear
                options={[
                  { label: t('canvas.workflow.variables.allFiles') || '全部文件', value: '' },
                  ...folders.map((folder) => ({
                    label: (
                      <span className="flex items-center gap-1">
                        <FolderOutlined />
                        {folder.name}
                      </span>
                    ),
                    value: folder.folderId,
                  })),
                ]}
              />
            </div>

            {isFolderSelected && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-refly-text-0 font-medium">
                    {t('canvas.workflow.variables.selectFiles') || '选择文件'}
                    <span className="text-refly-text-2 ml-1 font-normal">
                      (
                      {t('canvas.workflow.variables.selectedCount', {
                        count: selectedFileIds.length,
                      }) || `已选 ${selectedFileIds.length} 个`}
                      )
                    </span>
                  </div>
                </div>

                <div className="max-h-[340px] overflow-hidden border border-refly-Card-Border rounded-lg flex flex-col">
                  <div className="flex-shrink-0 px-3 py-2 bg-refly-bg-control-z0 border-b border-refly-Card-Border">
                    <div className="flex items-center justify-between">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartiallySelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={mediaFiles.length === 0}
                      >
                        <span className="text-sm text-refly-text-0">
                          {t('canvas.workflow.variables.selectAll') || '全选'}
                        </span>
                      </Checkbox>
                      <span className="text-xs text-refly-text-2">
                        {selectedInFolder} / {mediaFiles.length}
                      </span>
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    <Spin spinning={loadingFiles}>
                      {mediaFiles.length === 0 ? (
                        <div className="p-4">
                          <Empty
                            description={t('canvas.workflow.variables.noFiles') || '暂无文件'}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          {mediaFiles.map((file) => (
                            <div
                              key={file.fileId}
                              className={`flex items-center justify-between px-3 py-2 border-b border-refly-Card-Border last:border-b-0 hover:bg-refly-bg-control-z0 cursor-pointer transition-colors ${
                                selectedFileIds.includes(file.fileId)
                                  ? 'bg-refly-tertiary-hover'
                                  : ''
                              }`}
                              onClick={() => {
                                const isChecked = selectedFileIds.includes(file.fileId);
                                handleFileSelect(file.fileId, !isChecked);
                              }}
                            >
                              <Checkbox
                                checked={selectedFileIds.includes(file.fileId)}
                                onChange={(e) => handleFileSelect(file.fileId, e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex items-center gap-2 flex-1 ml-2 min-w-0">
                                <span className="text-refly-text-2">{getFileIcon(file.type)}</span>
                                <span className="text-sm text-refly-text-0 truncate flex-1">
                                  {file.name}
                                </span>
                              </div>
                              {file.size > 0 && (
                                <span className="text-xs text-refly-text-2 ml-2 flex-shrink-0">
                                  {getFileSize(file.size)}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Spin>
                  </div>
                </div>
              </div>
            )}

            {!isFolderSelected && (
              <div className="p-4 border border-dashed border-refly-Card-Border rounded-lg text-center">
                <Empty
                  description={t('canvas.workflow.variables.selectCategoryFirst') || '请先选择分类'}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </div>
            )}

            {/* Inline upload of new material (defaults to Uncategorized) */}
            <div className="flex flex-col gap-2">
              <div className="text-sm text-refly-text-0 font-medium">
                {t('canvas.workflow.variables.uploadNewMaterial') || '上传新素材'}
              </div>
              <Upload
                accept={acceptAttr}
                multiple
                showUploadList={false}
                fileList={fileList}
                beforeUpload={(file) => {
                  onFileUpload(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  {t('canvas.workflow.variables.uploadNewMaterial') || '上传新素材'}
                </Button>
              </Upload>
              <div className="text-xs text-refly-text-2">
                {t('canvas.workflow.variables.uploadToUncategorizedHint') ||
                  '新上传的素材将归入「未分类」'}
              </div>

              {fileList.length > 0 && (
                <div className="border border-refly-Card-Border rounded-lg flex flex-col max-h-[200px] overflow-y-auto">
                  {fileList.map((file) => (
                    <div
                      key={file.uid}
                      className="flex items-center justify-between px-3 py-2 border-b border-refly-Card-Border last:border-b-0"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-refly-text-2">
                          {getFileIcon(String(file.type || ''))}
                        </span>
                        <span className="text-sm text-refly-text-0 truncate flex-1">
                          {file.name}
                        </span>
                      </div>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onFileRemove(file)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Form.Item>

        {showError && hasNoSelection && (
          <div className="text-red-500 text-xs mt-1 mb-2">
            {t('canvas.workflow.variables.selectOrUploadFirst') || '请选择或上传素材'}
          </div>
        )}
      </>
    );
  },
);

ResourceTypeForm.displayName = 'ResourceTypeForm';
